// Products service. Reads + writes against `inventory.products`,
// `inventory.suppliers`, `inventory.product_lots`, `inventory.store_inventory`,
// `inventory.store_product_entitlements`, `pricing.store_product_prices`.
//
// Module: products (UI module ID; covers `inventory.*` + `pricing.*`).

import { supabase } from '../client';
import { requireOrgContext, type ActiveContext } from '../activeContext';

/**
 * Cast the typed supabase client to `any` for cross-schema queries against
 * the post-OH-migration schemas (`inventory`, `pricing`) that have not yet
 * been baked into `generated.ts`. Regenerate the types in a follow-up turn
 * (requires `supabase start` + `pnpm --filter @pc/db run db:types`) and
 * drop this shim.
 */
const sbUntyped = (): any => supabase() as unknown as any; // eslint-disable-line @typescript-eslint/no-explicit-any
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---------- explicit row types (until generated.ts ships these schemas) ----

export type ProductStatus = 'active' | 'paused' | 'archived';

export interface Product {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  category: string | null;
  sku: string | null;
  barcode: string | null;
  unit: string | null;
  base_price_cents: number;
  standard_price_cents: number;
  cost_cents: number | null;
  wholesale_price_cents: number | null;
  retail_price_cents: number | null;
  is_active: boolean;
  lot_tracking_enabled: boolean;
  exclude_label: boolean;
  deduct_from_profit_loss: boolean;
  outreach_days: number | null;
  hs_code: string | null;
  country_of_origin: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  status: ProductStatus;
  source_integration_key_id: string | null;
  source_external_id: string | null;
  source_label: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export type ProductInsert = Partial<Product> & {
  org_id: string;
  name: string;
  base_price_cents: number;
  standard_price_cents: number;
};

export type ProductUpdate = Partial<Product>;

export interface Supplier {
  id: string;
  org_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  notes: string | null;
  status: 'active' | 'paused' | 'archived';
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface StoreProductPrice {
  id: string;
  org_id: string;
  store_id: string;
  product_id: string;
  cost_price_cents: number;
  wholesale_price_cents: number;
  retail_price_cents: number;
  wholesale_enabled: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type SensitivePriceSheetVariant =
  | 'wholesale'
  | 'retail'
  | 'wholesale-retail'
  | 'wholesale-retail-cost';

export interface StoreInventory {
  id: string;
  org_id: string;
  store_id: string;
  product_id: string;
  quantity_on_hand: number;
  quantity_allocated_web: number;
  minimum_stock: number | null;
  created_at: string;
  updated_at: string;
}

export interface StoreProductEntitlement {
  id: string;
  org_id: string;
  store_id: string;
  product_id: string;
  granted_by: string | null;
  granted_at: string;
  archived_at: string | null;
}

export interface ProductLot {
  id: string;
  org_id: string;
  product_id: string;
  supplier_id: string | null;
  lot_number: string;
  received_date: string;
  expiration_date: string | null;
  sent_for_testing_date: string | null;
  testing_lab_name: string | null;
  testing_lab_phone: string | null;
  testing_lab_email: string | null;
  lab_id: string | null;
  lab_received_date: string | null;
  lab_received_confirmed_by: string | null;
  approved_for_sale_date: string | null;
  denied_date: string | null;
  purity_percent: number | null;
  result_mass_value: number | null;
  result_mass_unit: string | null;
  cap_color: string | null;
  neck_color: string | null;
  vial_size: string | null;
  quantity_issued: number;
  quantity_committed: number;
  status: 'received' | 'sent_for_testing' | 'awaiting_results' | 'approved' | 'denied' | 'depleted' | 'archived';
  is_archived: boolean;
  photo_url: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  source_integration_key_id: string | null;
  source_external_id: string | null;
  source_label: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ---------- products (org-level catalog) ----------------------------------

export interface ListProductsInput extends KeysetInput {
  search?: string;
  status?: ProductStatus;
  category?: string;
  includeArchived?: boolean;
}

export async function listProducts(
  ctx: ActiveContext,
  input: ListProductsInput = {},
): Promise<ListResult<Product>> {
  const { orgId } = requireOrgContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = sbUntyped()
    .schema('inventory')
    .from('products')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId);

  if (!input.includeArchived) q = q.neq('status', 'archived');
  if (input.status) q = q.eq('status', input.status);
  if (input.category) q = q.eq('category', input.category);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or([`name.ilike.${s}`, `sku.ilike.${s}`, `barcode.ilike.${s}`].join(','));
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as unknown as Product[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: count ?? null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getProduct(id: string): Promise<Product | null> {
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('products')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as unknown as Product | null;
}

export async function createProduct(
  ctx: ActiveContext,
  input: Omit<ProductInsert, 'org_id'> & { tags?: string[] },
): Promise<Product> {
  const { orgId } = requireOrgContext(ctx);
  const payload = {
    ...input,
    org_id: orgId,
    tags: input.tags ?? [],
    metadata: input.metadata ?? {},
    status: input.status ?? 'active',
    is_active: input.is_active ?? true,
    lot_tracking_enabled: input.lot_tracking_enabled ?? false,
    exclude_label: input.exclude_label ?? false,
    deduct_from_profit_loss: input.deduct_from_profit_loss ?? true,
  };
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('products')
    .insert(payload as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Product;
}

export async function updateProduct(
  id: string,
  patch: ProductUpdate,
): Promise<Product> {
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('products')
    .update(patch as never)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Product;
}

export async function archiveProduct(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('inventory')
    .from('products')
    .update({ status: 'archived', archived_at: new Date().toISOString() } as never)
    .eq('id', id);
  if (error) throw error;
}

// ---------- suppliers ------------------------------------------------------

export async function listSuppliers(ctx: ActiveContext): Promise<Supplier[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('suppliers')
    .select('*')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('name', { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as Supplier[];
}

// ---------- store pricing --------------------------------------------------

export async function listStorePricesForProduct(
  ctx: ActiveContext,
  productId: string,
): Promise<StoreProductPrice[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('pricing')
    .from('store_product_prices')
    .select('*')
    .eq('org_id', orgId)
    .eq('product_id', productId)
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as StoreProductPrice[];
}

export async function upsertStorePrice(
  ctx: ActiveContext,
  input: {
    store_id: string;
    product_id: string;
    cost_price_cents: number;
    wholesale_price_cents: number;
    retail_price_cents: number;
    wholesale_enabled: boolean;
    notes?: string | null;
  },
): Promise<StoreProductPrice> {
  const { orgId } = requireOrgContext(ctx);
  // The unique index on pricing.store_product_prices is (store_id, product_id);
  // stores belong to exactly one org via FK so the org_id is implied. PostgREST
  // ON CONFLICT must reference an actual unique index, so we use the real
  // column list here. RLS still enforces tenancy via the tenant_isolation
  // policy on org_id + store_id.
  const { data, error } = await sbUntyped()
    .schema('pricing')
    .from('store_product_prices')
    .upsert(
      { ...input, org_id: orgId } as never,
      { onConflict: 'store_id,product_id' },
    )
    .select()
    .single();
  if (error) throw error;
  return data as unknown as StoreProductPrice;
}

export async function logSensitivePriceSheetPrint(
  ctx: ActiveContext,
  input: {
    product_id?: string | null;
    sheet_variant: SensitivePriceSheetVariant;
    store_ids?: string[];
    includes_wholesale: boolean;
    includes_cost: boolean;
    watermark_name?: string | null;
  },
): Promise<void> {
  const { orgId } = requireOrgContext(ctx);
  const { error } = await sbUntyped()
    .schema('pricing')
    .rpc('rpc_log_sensitive_price_sheet_print', {
      p_org_id: orgId,
      p_product_id: input.product_id ?? null,
      p_sheet_variant: input.sheet_variant,
      p_store_ids: input.store_ids ?? null,
      p_includes_wholesale: input.includes_wholesale,
      p_includes_cost: input.includes_cost,
      p_watermark_name: input.watermark_name ?? null,
    });
  if (error) throw error;
}

// ---------- entitlements ---------------------------------------------------

export async function listEntitlementsForProduct(
  ctx: ActiveContext,
  productId: string,
): Promise<StoreProductEntitlement[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('store_product_entitlements')
    .select('*')
    .eq('org_id', orgId)
    .eq('product_id', productId)
    .is('archived_at', null)
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as StoreProductEntitlement[];
}

export async function grantEntitlement(
  ctx: ActiveContext,
  input: { store_id: string; product_id: string },
): Promise<StoreProductEntitlement> {
  const { orgId } = requireOrgContext(ctx);
  const sb = sbUntyped().schema('inventory').from('store_product_entitlements');

  // The unique index on inventory.store_product_entitlements is partial
  // (`WHERE archived_at IS NULL`), so PostgREST `upsert` cannot infer it.
  // Mirror migrate.rpc_import_store_entitlement: return the active row if
  // it exists, otherwise reactivate the most recently archived row, and
  // only insert if neither is present.
  const { data: active, error: activeError } = await sb
    .select('*')
    .eq('org_id', orgId)
    .eq('store_id', input.store_id)
    .eq('product_id', input.product_id)
    .is('archived_at', null)
    .limit(1)
    .maybeSingle();
  if (activeError) throw activeError;
  if (active) return active as unknown as StoreProductEntitlement;

  const { data: archived, error: archivedError } = await sb
    .select('id')
    .eq('org_id', orgId)
    .eq('store_id', input.store_id)
    .eq('product_id', input.product_id)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (archivedError) throw archivedError;

  if (archived) {
    const { data: revived, error: reviveError } = await sbUntyped()
      .schema('inventory')
      .from('store_product_entitlements')
      .update({
        archived_at: null,
        archived_by: null,
        granted_at: new Date().toISOString(),
      } as never)
      .eq('id', (archived as { id: string }).id)
      .select()
      .single();
    if (reviveError) throw reviveError;
    return revived as unknown as StoreProductEntitlement;
  }

  const { data: inserted, error: insertError } = await sbUntyped()
    .schema('inventory')
    .from('store_product_entitlements')
    .insert({ ...input, org_id: orgId } as never)
    .select()
    .single();
  if (insertError) throw insertError;
  return inserted as unknown as StoreProductEntitlement;
}

export async function revokeEntitlement(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('inventory')
    .from('store_product_entitlements')
    .update({ archived_at: new Date().toISOString() } as never)
    .eq('id', id);
  if (error) throw error;
}

// ---------- inventory + lots ----------------------------------------------

export async function listStoreInventoryForProduct(
  ctx: ActiveContext,
  productId: string,
): Promise<StoreInventory[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('store_inventory')
    .select('*')
    .eq('org_id', orgId)
    .eq('product_id', productId)
    .limit(500);
  if (error) throw error;
  return (data ?? []) as unknown as StoreInventory[];
}

export async function listLotsForProduct(
  ctx: ActiveContext,
  productId: string,
): Promise<ProductLot[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .select('*')
    .eq('org_id', orgId)
    .eq('product_id', productId)
    .eq('is_archived', false)
    .order('received_date', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as ProductLot[];
}

// ---------- lot management (org-wide list + CRUD) -------------------------
//
// The Lot Management module reads / writes against `inventory.product_lots`
// at the org level. Stores access lots indirectly through
// `inventory.store_inventory`; lots themselves are NOT store-scoped.

export type ProductLotStatus = ProductLot['status'];

export interface LotRow extends ProductLot {
  product_name: string | null;
  product_sku: string | null;
  supplier_name: string | null;
}

export interface ListLotsInput extends KeysetInput {
  search?: string;
  status?: ProductLotStatus | ProductLotStatus[];
  productId?: string;
  supplierId?: string;
  expiringWithinDays?: number;
  includeArchived?: boolean;
}

/**
 * Keyset-paginated list of lots for the active org. The select joins on
 * `inventory.products` and `inventory.suppliers` so the list can render
 * a product / supplier label without a follow-up N+1 fetch.
 */
export async function listLots(
  ctx: ActiveContext,
  input: ListLotsInput = {},
): Promise<ListResult<LotRow>> {
  const { orgId } = requireOrgContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .select(
      'id, org_id, product_id, supplier_id, lot_number, received_date, expiration_date, sent_for_testing_date, testing_lab_name, testing_lab_phone, testing_lab_email, lab_id, lab_received_date, lab_received_confirmed_by, approved_for_sale_date, denied_date, purity_percent, result_mass_value, result_mass_unit, cap_color, neck_color, vial_size, quantity_issued, quantity_committed, status, is_archived, photo_url, notes, metadata, source_integration_key_id, source_external_id, source_label, created_by, created_at, updated_at, product:products!product_id(name, sku), supplier:suppliers!supplier_id(name)',
      selectCountOption(input.countMode),
    )
    .eq('org_id', orgId);

  if (!input.includeArchived) q = q.eq('is_archived', false);
  if (input.productId) q = q.eq('product_id', input.productId);
  if (input.supplierId) q = q.eq('supplier_id', input.supplierId);
  if (Array.isArray(input.status) && input.status.length > 0)
    q = q.in('status', input.status);
  else if (typeof input.status === 'string') q = q.eq('status', input.status);

  if (
    typeof input.expiringWithinDays === 'number' &&
    input.expiringWithinDays > 0
  ) {
    const horizon = new Date();
    horizon.setUTCDate(horizon.getUTCDate() + input.expiringWithinDays);
    q = q
      .not('expiration_date', 'is', null)
      .lte('expiration_date', horizon.toISOString().slice(0, 10));
  }

  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      [`lot_number.ilike.${s}`, `notes.ilike.${s}`, `testing_lab_name.ilike.${s}`].join(','),
    );
  }

  q = applyKeyset(q, input.cursor, 'received_date');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows: LotRow[] = (data ?? []).map((r: Record<string, unknown>) => ({
    ...(r as unknown as ProductLot),
    product_name:
      (r.product as { name?: string | null } | null)?.name ?? null,
    product_sku: (r.product as { sku?: string | null } | null)?.sku ?? null,
    supplier_name:
      (r.supplier as { name?: string | null } | null)?.name ?? null,
  }));
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'received_date',
    ),
    total: count ?? null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getLot(id: string): Promise<LotRow | null> {
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .select(
      '*, product:products!product_id(name, sku), supplier:suppliers!supplier_id(name)',
    )
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const r = data as Record<string, unknown>;
  return {
    ...(r as unknown as ProductLot),
    product_name: (r.product as { name?: string | null } | null)?.name ?? null,
    product_sku: (r.product as { sku?: string | null } | null)?.sku ?? null,
    supplier_name:
      (r.supplier as { name?: string | null } | null)?.name ?? null,
  };
}

export interface CreateLotInput {
  product_id: string;
  lot_number: string;
  received_date: string;
  expiration_date?: string | null;
  supplier_id?: string | null;
  cap_color?: string | null;
  neck_color?: string | null;
  vial_size?: string | null;
  quantity_issued?: number;
  notes?: string | null;
}

export async function createLot(
  ctx: ActiveContext,
  input: CreateLotInput,
): Promise<ProductLot> {
  const { orgId } = requireOrgContext(ctx);
  const payload = {
    org_id: orgId,
    product_id: input.product_id,
    lot_number: input.lot_number.trim(),
    received_date: input.received_date,
    expiration_date: input.expiration_date ?? null,
    supplier_id: input.supplier_id ?? null,
    cap_color: input.cap_color ?? null,
    neck_color: input.neck_color ?? null,
    vial_size: input.vial_size ?? null,
    quantity_issued: input.quantity_issued ?? 0,
    notes: input.notes ?? null,
    status: 'received' as ProductLotStatus,
  };
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .insert(payload as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ProductLot;
}

export type LotPatch = Partial<{
  lot_number: string;
  received_date: string;
  expiration_date: string | null;
  supplier_id: string | null;
  sent_for_testing_date: string | null;
  testing_lab_name: string | null;
  testing_lab_phone: string | null;
  testing_lab_email: string | null;
  lab_received_date: string | null;
  approved_for_sale_date: string | null;
  denied_date: string | null;
  purity_percent: number | null;
  result_mass_value: number | null;
  result_mass_unit: string | null;
  cap_color: string | null;
  neck_color: string | null;
  vial_size: string | null;
  quantity_issued: number;
  quantity_committed: number;
  status: ProductLotStatus;
  photo_url: string | null;
  notes: string | null;
}>;

export async function updateLot(id: string, patch: LotPatch): Promise<ProductLot> {
  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .update(patch as never)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ProductLot;
}

export async function archiveLot(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('inventory')
    .from('product_lots')
    .update({ is_archived: true, status: 'archived' } as never)
    .eq('id', id);
  if (error) throw error;
}

// ---------- lot photo storage (private bucket `product-lots`) -------------
//
// Path layout (the first segment is the tenant org_id so the storage RLS
// policy `product_lots_tenant_isolation` from
// `supabase/migrations/20260625000000_lot_photos_bucket.sql` can route
// every read/write through `core.user_has_org_access`):
//
//   <org_id>/<product_id>/<lot_id>/intake_photo_<ts>.<ext>

const LOT_PHOTO_BUCKET = 'product-lots';

export async function uploadLotPhoto(
  ctx: ActiveContext,
  input: { productId: string; lotId: string; file: File },
): Promise<string> {
  const { orgId } = requireOrgContext(ctx);
  const ext = (input.file.name.split('.').pop() || 'jpg')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const safeExt = ext.length > 0 && ext.length <= 5 ? ext : 'jpg';
  const path = `${orgId}/${input.productId}/${input.lotId}/intake_photo_${Date.now()}.${safeExt}`;
  const { error } = await sbUntyped()
    .storage.from(LOT_PHOTO_BUCKET)
    .upload(path, input.file, {
      contentType: input.file.type || 'image/jpeg',
      cacheControl: '3600',
      upsert: true,
    });
  if (error) throw error;
  return path;
}

export async function getLotPhotoSignedUrl(
  path: string,
  expiresIn = 3600,
): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await sbUntyped()
    .storage.from(LOT_PHOTO_BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) return null;
  const url = (data as { signedUrl?: string } | null)?.signedUrl ?? null;
  return url;
}

export async function deleteLotPhoto(path: string): Promise<void> {
  if (!path) return;
  const { error } = await sbUntyped()
    .storage.from(LOT_PHOTO_BUCKET)
    .remove([path]);
  if (error) throw error;
}

export interface LotSummary {
  total: number;
  by_status: Record<ProductLotStatus, number>;
  expiring_within_30d: number;
  expiring_within_90d: number;
}

const ZERO_STATUS_COUNTS: Record<ProductLotStatus, number> = {
  received: 0,
  sent_for_testing: 0,
  awaiting_results: 0,
  approved: 0,
  denied: 0,
  depleted: 0,
  archived: 0,
};

/**
 * One-shot counts driving the dashboard tile. Three small head-count
 * queries — cheaper than scanning the full table — plus one short
 * select for the status breakdown across active lots.
 */
export async function getLotSummary(ctx: ActiveContext): Promise<LotSummary> {
  const { orgId } = requireOrgContext(ctx);
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date();
  in30.setUTCDate(in30.getUTCDate() + 30);
  const in90 = new Date();
  in90.setUTCDate(in90.getUTCDate() + 90);

  const [
    { count: total },
    { count: exp30 },
    { count: exp90 },
    { data: statusRows },
  ] = await Promise.all([
    sbUntyped()
      .schema('inventory')
      .from('product_lots')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('is_archived', false),
    sbUntyped()
      .schema('inventory')
      .from('product_lots')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('is_archived', false)
      .not('expiration_date', 'is', null)
      .gte('expiration_date', today)
      .lte('expiration_date', in30.toISOString().slice(0, 10)),
    sbUntyped()
      .schema('inventory')
      .from('product_lots')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('is_archived', false)
      .not('expiration_date', 'is', null)
      .gte('expiration_date', today)
      .lte('expiration_date', in90.toISOString().slice(0, 10)),
    sbUntyped()
      .schema('inventory')
      .from('product_lots')
      .select('status')
      .eq('org_id', orgId)
      .eq('is_archived', false)
      .limit(10_000),
  ]);

  const by_status = { ...ZERO_STATUS_COUNTS };
  for (const r of (statusRows ?? []) as Array<{ status: ProductLotStatus }>) {
    by_status[r.status] = (by_status[r.status] ?? 0) + 1;
  }
  return {
    total: total ?? 0,
    by_status,
    expiring_within_30d: exp30 ?? 0,
    expiring_within_90d: exp90 ?? 0,
  };
}

// ---------- client price overrides (read for products UI) -----------------

export interface ClientPriceOverride {
  id: string;
  org_id: string;
  client_id: string;
  product_id: string;
  custom_price_cents: number;
  effective_date: string | null;
  expiry_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientPriceOverrideEnriched extends ClientPriceOverride {
  product_name: string;
  product_sku: string | null;
}

export async function getProductNamesByIds(
  ctx: ActiveContext,
  ids: string[],
): Promise<Map<string, { name: string; sku: string | null }>> {
  const { orgId } = requireOrgContext(ctx);
  const unique = [...new Set(ids.filter(Boolean))];
  const out = new Map<string, { name: string; sku: string | null }>();
  if (unique.length === 0) return out;

  const { data, error } = await sbUntyped()
    .schema('inventory')
    .from('products')
    .select('id, name, sku')
    .eq('org_id', orgId)
    .in('id', unique);
  if (error) throw error;

  for (const row of (data ?? []) as Array<{ id: string; name: string; sku: string | null }>) {
    out.set(row.id, { name: row.name, sku: row.sku });
  }
  return out;
}

export async function listClientOverridesForProduct(
  ctx: ActiveContext,
  productId: string,
): Promise<ClientPriceOverride[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('pricing')
    .from('client_price_overrides')
    .select('*')
    .eq('org_id', orgId)
    .eq('product_id', productId)
    .order('updated_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as ClientPriceOverride[];
}

export async function listClientOverridesForClient(
  ctx: ActiveContext,
  clientId: string,
): Promise<ClientPriceOverride[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await sbUntyped()
    .schema('pricing')
    .from('client_price_overrides')
    .select('*')
    .eq('org_id', orgId)
    .eq('client_id', clientId)
    .order('updated_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as ClientPriceOverride[];
}

export async function listClientOverridesForClientEnriched(
  ctx: ActiveContext,
  clientId: string,
): Promise<ClientPriceOverrideEnriched[]> {
  const rows = await listClientOverridesForClient(ctx, clientId);
  if (rows.length === 0) return [];

  const productIds = rows.map((r) => r.product_id);
  const namesById = await getProductNamesByIds(ctx, productIds);

  return rows.map((r) => {
    const product = namesById.get(r.product_id);
    return {
      ...r,
      product_name: product?.name ?? r.product_id.slice(0, 8),
      product_sku: product?.sku ?? null,
    };
  });
}

export async function upsertClientPriceOverride(
  ctx: ActiveContext,
  input: {
    id?: string;
    client_id: string;
    product_id: string;
    custom_price_cents: number;
    effective_date?: string | null;
    expiry_date?: string | null;
    notes?: string | null;
  },
): Promise<ClientPriceOverride> {
  const { orgId } = requireOrgContext(ctx);
  if (input.id) {
    const { data, error } = await sbUntyped()
      .schema('pricing')
      .from('client_price_overrides')
      .update({
        custom_price_cents: input.custom_price_cents,
        effective_date: input.effective_date ?? null,
        expiry_date: input.expiry_date ?? null,
        notes: input.notes ?? null,
      } as never)
      .eq('id', input.id)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as ClientPriceOverride;
  }
  const { data, error } = await sbUntyped()
    .schema('pricing')
    .from('client_price_overrides')
    .insert({
      org_id: orgId,
      client_id: input.client_id,
      product_id: input.product_id,
      custom_price_cents: input.custom_price_cents,
      effective_date: input.effective_date ?? null,
      expiry_date: input.expiry_date ?? null,
      notes: input.notes ?? null,
    } as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ClientPriceOverride;
}

export async function deleteClientPriceOverride(id: string): Promise<void> {
  const { error } = await sbUntyped()
    .schema('pricing')
    .from('client_price_overrides')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ---------- product summary (for dashboard widget) -------------------------

export interface ProductSummary {
  active_product_count: number;
  total_lot_count: number;
}

export async function getProductSummary(ctx: ActiveContext): Promise<ProductSummary> {
  const { orgId } = requireOrgContext(ctx);
  const [{ count: activeCount }, { count: lotCount }] = await Promise.all([
    sbUntyped()
      .schema('inventory')
      .from('products')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'active'),
    sbUntyped()
      .schema('inventory')
      .from('product_lots')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('is_archived', false),
  ]);
  return {
    active_product_count: activeCount ?? 0,
    total_lot_count: lotCount ?? 0,
  };
}
